import {
  appointmentTypeInputSchema,
  chatAttachmentMimeTypes,
  MAX_CHAT_ATTACHMENT_BYTES,
  MAX_PROFILE_PHOTO_BYTES,
  MAX_SCHEDULING_ALERT_NOTE_LENGTH,
  patientInputSchema,
  profilePhotoMimeTypes,
  type PatientInput,
} from '@clinic/shared';
import { z } from 'zod';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  Timestamp,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type QueryConstraint,
  type Unsubscribe,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getBlob, ref, uploadBytes } from 'firebase/storage';
import type {
  Appointment,
  AppointmentDraft,
  AppointmentType,
  AvailabilityRule,
  ChatAttachment,
  ChatReference,
  ClinicData,
  ClinicNotification,
  ClinicTask,
  ClinicUser,
  Conversation,
  ConversationSummary,
  DashboardOperationalData,
  EmailDeliveryEvent,
  FinanceMonth,
  OutstandingAppointmentsCursor,
  OutstandingAppointmentsPage,
  Patient,
  PatientAppointmentCursor,
  PatientAppointmentPage,
  PatientDetailsResult,
  PatientDraft,
  PatientListCursor,
  PatientListPage,
  PatientPaymentCursor,
  PatientPaymentHistoryPage,
  PatientSchedulingAlertUpdate,
  ScheduleBlock,
  ScheduleBlockDraft,
  ScheduleRangeResult,
  TaskDraft,
  TaskActivity,
  TaskUpdateDraft,
} from '../domain/types';
import { onlyDigits } from '../domain/formatters';
import { isOutstandingCompletedAppointment } from '../domain/operationalData';
import { getFirebaseServices } from './client';
import { toPortugueseError } from './errors';
import { tokens } from '../theme/tokens';

const idSchema = z.string().min(1).max(160);
const isoSchema = z.string().datetime();
const lifecycleSchema = z.enum([
  'pending_scheduling_confirmation',
  'scheduled',
  'confirmed',
  'completed',
  'no_show',
  'rescheduled',
  'cancelled',
]);
const paymentSchema = z.enum([
  'pending',
  'partially_paid',
  'paid',
  'partially_refunded',
  'refunded',
]);

const patientDocumentSchema = z
  .object({
    clinicId: idSchema,
    fullName: z.string().default(''),
    birthDate: z.string().default(''),
    cpf: z.string().default(''),
    primaryPhone: z.string().default(''),
    secondaryPhone: z.string().default(''),
    emergencyPhone1: z.string().default(''),
    emergencyPhone2: z.string().default(''),
    email: z.string().default(''),
    cep: z.string().default(''),
    street: z.string().default(''),
    number: z.string().default(''),
    complement: z.string().default(''),
    neighborhood: z.string().default(''),
    city: z.string().default(''),
    state: z.string().default(''),
    observations: z.string().default(''),
    socialCareEnabled: z.boolean().default(false),
    socialPrices: z
      .array(
        z.object({
          appointmentTypeId: idSchema,
          priceCents: z.number().int().nonnegative(),
        }),
      )
      .default([]),
    dataQualityIssues: z.array(z.string()).default([]),
    outstandingBalanceCents: z.number().int().nonnegative().default(0),
    requiresAdvancePayment: z.boolean().default(false),
    schedulingAlertMode: z
      .enum(['dashboard_and_scheduling', 'scheduling_only', 'discarded'])
      .optional(),
    schedulingAlertNote: z.string().max(MAX_SCHEDULING_ALERT_NOTE_LENGTH).default(''),
    status: z.string().default('active'),
    inactiveReason: z.enum(['medical_discharge', 'abandonment']).nullable().optional(),
  })
  .passthrough();

const memberDocumentSchema = z
  .object({
    clinicId: idSchema,
    displayName: z.string().trim().min(1).max(120),
    email: z.string().email().or(z.literal('')).default(''),
    role: z.enum(['medico', 'secretaria']),
    active: z.boolean(),
    avatarColor: z.string().optional(),
    photoPath: z.string().min(1).max(512).optional(),
  })
  .passthrough();

const appointmentTypeDocumentSchema = appointmentTypeInputSchema.extend({
  clinicId: idSchema,
});

const localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const availabilityRuleDocumentSchema = z
  .object({
    clinicId: idSchema,
    providerId: idSchema,
    weekday: z.number().int().min(0).max(6),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
    validFromLocalDate: localDateSchema,
    validUntilLocalDate: localDateSchema.nullable().optional(),
    active: z.boolean(),
  })
  .passthrough();

const appointmentDocumentSchema = z
  .object({
    clinicId: idSchema,
    patientId: idSchema,
    providerId: idSchema,
    appointmentTypeId: idSchema,
    startAt: isoSchema,
    endAt: isoSchema,
    lifecycleStatus: lifecycleSchema,
    paymentStatus: paymentSchema,
    priceSnapshotCents: z.number().int().nonnegative(),
    priceSource: z.enum(['standard', 'social', 'social_override', 'appointment_type_default']),
    appointmentTypeSnapshot: z.object({
      name: z.string(),
      durationMinutes: z.number().int().positive(),
      calendarColor: z.string(),
    }),
    amountPaidCents: z.number().int().nonnegative().default(0),
    amountRefundedCents: z.number().int().nonnegative().default(0),
    balanceCents: z.number().int().nonnegative(),
    expectedPaymentAt: isoSchema.nullable().optional(),
    telehealthRoomUrl: z.string().url().optional(),
    emailSentAt: isoSchema.optional(),
  })
  .passthrough();

const blockDocumentSchema = z
  .object({
    clinicId: idSchema,
    startAt: isoSchema,
    endAt: isoSchema,
    reason: z.string().default(''),
    status: z.enum(['active', 'cancelled', 'retired']).default('active'),
    createdBy: z.string().default(''),
    updatedBy: z.string().default(''),
  })
  .passthrough();

const taskDocumentSchema = z
  .object({
    clinicId: idSchema,
    title: z.string(),
    description: z.string().default(''),
    creatorId: idSchema,
    assigneeId: idSchema,
    status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
    priority: z.enum(['low', 'normal', 'high', 'urgent']),
    dueAt: isoSchema.nullable().optional(),
    createdAt: isoSchema,
    updatedAt: isoSchema,
    completedAt: isoSchema.nullable().optional(),
    patientId: idSchema.nullable().optional(),
    appointmentId: idSchema.nullable().optional(),
  })
  .passthrough();

const taskEventDocumentSchema = z
  .object({
    clinicId: idSchema,
    taskId: idSchema,
    eventType: z.string(),
    actorId: idSchema,
    before: z.record(z.string(), z.unknown()).default({}),
    after: z.record(z.string(), z.unknown()).default({}),
    occurredAt: isoSchema,
  })
  .passthrough();

const appointmentEventDocumentSchema = z
  .object({
    clinicId: idSchema,
    appointmentId: idSchema,
    eventType: z.string(),
    actorId: idSchema,
    occurredAt: isoSchema,
    metadata: z.record(z.string(), z.unknown()).default({}),
  })
  .passthrough();

const paymentLedgerDocumentSchema = z
  .object({
    clinicId: idSchema,
    patientId: idSchema,
    appointmentId: idSchema,
    entryType: z.enum(['payment', 'refund']),
    amountCents: z.number().int().positive(),
    occurredAt: isoSchema,
    actorId: idSchema,
  })
  .passthrough();

export const emailDeliveryEventDocumentSchema = z
  .object({
    clinicId: idSchema,
    appointmentId: idSchema,
    recipientEmail: z.string().email(),
    status: z.enum(['processing', 'queued', 'sent', 'failed']),
    requestedAt: isoSchema,
    sentAt: isoSchema.nullable().optional(),
    templateVersion: z.string().trim().min(1).max(120),
    attemptNumber: z.number().int().positive().default(1),
  })
  .passthrough();

interface TaskEventDescriptionInput {
  eventType: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

const taskStatusLabels: Record<string, string> = {
  pending: 'pendente',
  in_progress: 'em andamento',
  completed: 'concluída',
  cancelled: 'cancelada',
};

const taskPriorityLabels: Record<string, string> = {
  low: 'baixa',
  normal: 'normal',
  high: 'alta',
  urgent: 'urgente',
};

export function describeTaskEvent(
  event: TaskEventDescriptionInput,
  resolveUserName: (userId: string) => string | undefined = () => undefined,
): string {
  const changes: string[] = [];
  if (event.eventType === 'task.created') changes.push('Tarefa criada');
  if (event.eventType === 'task.completed') changes.push('Tarefa concluída');
  if (event.before.assigneeId !== event.after.assigneeId && event.after.assigneeId) {
    changes.push(
      `Responsável alterado para ${resolveUserName(String(event.after.assigneeId)) ?? 'outro profissional'}`,
    );
  }
  if (event.before.status !== event.after.status && event.after.status) {
    changes.push(
      `Situação alterada para ${taskStatusLabels[String(event.after.status)] ?? 'atualizada'}`,
    );
  }
  if (event.before.priority !== event.after.priority && event.after.priority) {
    changes.push(
      `Prioridade alterada para ${taskPriorityLabels[String(event.after.priority)] ?? 'atualizada'}`,
    );
  }
  if (event.before.dueAt !== event.after.dueAt) {
    changes.push(event.after.dueAt ? 'Prazo atualizado' : 'Prazo removido');
  }
  return changes.join('. ') || 'Atividade da tarefa registrada';
}

export const conversationDocumentSchema = z
  .object({
    clinicId: idSchema,
    type: z.enum(['direct', 'clinic']),
    title: z.string().optional(),
    participantIds: z.array(idSchema).default([]),
    active: z.boolean().default(true),
    lastMessageAt: isoSchema.nullable().optional(),
    lastMessagePreview: z.string().max(120).default(''),
  })
  .passthrough();

export const conversationReadStateDocumentSchema = z
  .object({
    clinicId: idSchema,
    conversationId: idSchema,
    userId: idSchema,
    lastReadAt: isoSchema.nullable().optional(),
    lastReadMessageId: idSchema.nullable().optional(),
    unreadCount: z.number().int().nonnegative().default(0),
  })
  .passthrough();

const attachmentSchema = z
  .object({
    attachmentId: z.string().optional(),
    name: z.string().optional(),
    fileName: z.string().optional(),
    mimeType: z.string().optional(),
    contentType: z.string().optional(),
    size: z.number().optional(),
    sizeBytes: z.number().optional(),
    url: z.string().optional(),
    downloadUrl: z.string().optional(),
    reservationId: z.string().optional(),
    storagePath: z.string().optional(),
    originalFileName: z.string().optional(),
  })
  .nullable()
  .optional();

const messageDocumentSchema = z
  .object({
    clinicId: idSchema,
    conversationId: idSchema,
    senderId: idSchema,
    senderName: z.string().optional(),
    text: z.string().default(''),
    sentAt: isoSchema,
    status: z.enum(['sending', 'sent', 'failed']).default('sent'),
    deliveryState: z.enum(['sending', 'sent', 'failed']).optional(),
    attachment: attachmentSchema,
    reference: z
      .object({ type: z.enum(['patient', 'task']), id: idSchema })
      .nullable()
      .optional(),
  })
  .passthrough();

const notificationDocumentSchema = z
  .object({
    clinicId: idSchema,
    recipientId: idSchema,
    type: z.string(),
    message: z.string(),
    createdAt: isoSchema,
    readAt: isoSchema.nullable().optional(),
    reference: z.object({ type: z.string(), id: z.string() }).nullable().optional(),
  })
  .passthrough();

const financePrivateDocumentSchema = z
  .object({
    clinicId: idSchema,
    year: z.number().int(),
    month: z.number().int().min(1).max(12),
    forecastCents: z.number().int(),
    grossPaymentsCents: z.number().int(),
    refundsCents: z.number().int(),
    netReceiptsCents: z.number().int(),
  })
  .passthrough();

const patientMutationResultSchema = z.object({ patientId: idSchema });
const patientBulkMutationResultSchema = z.object({ patientIds: z.array(idSchema).max(100) });
const appointmentCreateResultSchema = z.object({
  appointmentId: idSchema,
  startAt: isoSchema,
  endAt: isoSchema,
  priceSnapshotCents: z.number().int().nonnegative(),
  priceSource: z.enum(['social_override', 'appointment_type_default']),
});
export const ledgerMutationResultSchema = z.object({
  ledgerEntryId: idSchema,
  paymentState: paymentSchema,
  amountPaidCents: z.number().int().nonnegative(),
  amountRefundedCents: z.number().int().nonnegative(),
  balanceCents: z.number().int().nonnegative(),
});
const cancellationMutationResultSchema = ledgerMutationResultSchema
  .extend({
    appointmentId: idSchema,
    lifecycleStatus: z.literal('cancelled'),
    ledgerEntryId: idSchema.nullable(),
  })
  .passthrough();
const taskMutationResultSchema = z.object({ taskId: idSchema });
const searchResultSchema = z.object({
  items: z.array(
    z.object({
      patientId: idSchema,
      fullName: z.string(),
      birthDate: z.string(),
      cpf: z.string(),
      primaryPhone: z.string(),
      email: z.string(),
      requiresAdvancePayment: z.boolean(),
      outstandingBalanceCents: z.number().int().nonnegative(),
      active: z.boolean(),
    }),
  ),
  nextOffset: z.number().int().nonnegative().nullable(),
});

export type PatientSearchResult = z.infer<typeof searchResultSchema>['items'][number];

const attachmentReservationSchema = z.object({
  reservationId: idSchema,
  messageId: idSchema,
  storagePath: z.string().min(1),
  requiredMetadata: z.record(z.string(), z.string()),
  expiresAt: isoSchema,
});

export function selectPrimaryProviderId(users: ClinicUser[], session: ClinicUser): string {
  const provider = users.find((user) => user.role === 'medico');
  if (provider) return provider.id;
  if (session.role === 'medico') return session.id;
  throw new Error('Nenhum médico ativo está disponível para receber agendamentos.');
}

function normalizeFirestoreValue(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(normalizeFirestoreValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [key, normalizeFirestoreValue(nested)]),
    );
  }
  return value;
}

function wasChangedByExternalActor(raw: DocumentData, currentUserId: string): boolean {
  const actor = [raw.updatedBy, raw.createdBy, raw.actorId].find(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
  return actor == null || actor !== currentUserId;
}

function parseRemote<T>(schema: z.ZodType<T>, value: unknown): T {
  const result = schema.safeParse(value);
  if (!result.success) {
    throw new Error(
      'Os dados recebidos estão incompletos. Atualize a página ou fale com o suporte.',
    );
  }
  return result.data;
}

async function listDocuments<T>(
  path: string,
  constraints: QueryConstraint[],
  schema: z.ZodType<T>,
): Promise<Array<{ id: string; data: T }>> {
  const { db } = getFirebaseServices();
  const snapshot = await getDocs(query(collection(db, path), ...constraints));
  return snapshot.docs.map((document) => ({
    id: document.id,
    data: parseRemote(schema, normalizeFirestoreValue(document.data())),
  }));
}

async function listDocumentsPaginated<T>(
  path: string,
  constraints: QueryConstraint[],
  schema: z.ZodType<T>,
  safetyCap: number,
  maximumPageSize = 100,
  overflowMessage = 'Não foi possível carregar todos os registros com segurança. Reduza o período quando disponível ou fale com o suporte.',
): Promise<Array<{ id: string; data: T }>> {
  const { db } = getFirebaseServices();
  const results: Array<{ id: string; data: T }> = [];
  let cursor: QueryDocumentSnapshot<DocumentData> | undefined;
  while (results.length < safetyCap) {
    const remaining = safetyCap - results.length;
    const pageSize = Math.min(maximumPageSize, remaining);
    const page = await getDocs(
      query(
        collection(db, path),
        ...constraints,
        ...(cursor ? [startAfter(cursor)] : []),
        limit(pageSize),
      ),
    );
    results.push(
      ...page.docs.map((document) => ({
        id: document.id,
        data: parseRemote(schema, normalizeFirestoreValue(document.data())),
      })),
    );
    if (page.docs.length < pageSize) return results;
    cursor = page.docs.at(-1);
  }
  throw new Error(overflowMessage);
}

async function invoke<TRequest, TResponse>(
  name: string,
  payload: TRequest,
  responseSchema: z.ZodType<TResponse>,
): Promise<TResponse> {
  try {
    const { functions } = getFirebaseServices();
    const callable = httpsCallable<TRequest, unknown>(functions, name);
    const result = await callable(payload);
    return parseRemote(responseSchema, result.data);
  } catch (error) {
    throw toPortugueseError(error, 'Não foi possível concluir a ação agora. Tente novamente.');
  }
}

function patientDraftToWire(patient: PatientDraft): PatientInput {
  return patientInputSchema.parse({
    fullName: patient.fullName,
    birthDate: patient.birthDate,
    cpf: patient.cpf,
    primaryPhone: patient.primaryPhone,
    secondaryPhone: patient.secondaryPhone,
    emergencyPhone1: patient.emergencyPhone1,
    emergencyPhone2: patient.emergencyPhone2,
    email: patient.email,
    observations: patient.observations,
    cep: patient.address.postalCode,
    street: patient.address.street,
    number: patient.address.number,
    complement: patient.address.complement,
    neighborhood: patient.address.district,
    city: patient.address.city,
    state: patient.address.state,
    socialCareEnabled: patient.socialCareEnabled,
    socialPrices: Object.entries(patient.socialPricesCents).map(
      ([appointmentTypeId, priceCents]) => ({ appointmentTypeId, priceCents }),
    ),
  });
}

function patientFromWire(id: string, wire: z.infer<typeof patientDocumentSchema>): Patient {
  return {
    id,
    fullName: wire.fullName,
    birthDate: wire.birthDate,
    cpf: wire.cpf,
    cpfDigits: onlyDigits(wire.cpf),
    primaryPhone: wire.primaryPhone,
    primaryPhoneDigits: onlyDigits(wire.primaryPhone),
    secondaryPhone: wire.secondaryPhone,
    emergencyPhone1: wire.emergencyPhone1,
    emergencyPhone2: wire.emergencyPhone2,
    email: wire.email,
    observations: wire.observations,
    address: {
      postalCode: wire.cep,
      street: wire.street,
      number: wire.number,
      complement: wire.complement,
      district: wire.neighborhood,
      city: wire.city,
      state: wire.state,
    },
    socialCareEnabled: wire.socialCareEnabled,
    socialPricesCents: Object.fromEntries(
      wire.socialPrices.map((price) => [price.appointmentTypeId, price.priceCents]),
    ),
    requiresAdvancePayment: wire.requiresAdvancePayment,
    schedulingAlertMode:
      wire.schedulingAlertMode ??
      (wire.requiresAdvancePayment ? 'dashboard_and_scheduling' : 'discarded'),
    schedulingAlertNote: wire.schedulingAlertNote,
    outstandingBalanceCents: wire.outstandingBalanceCents,
    active: wire.status === 'active',
    inactiveReason: wire.inactiveReason ?? null,
  };
}

function appointmentFromWire(
  id: string,
  item: z.infer<typeof appointmentDocumentSchema>,
  patientName: string,
): Appointment {
  return {
    id,
    patientId: item.patientId,
    patientName,
    appointmentTypeId: item.appointmentTypeId,
    appointmentTypeName: item.appointmentTypeSnapshot.name,
    providerId: item.providerId,
    startAt: item.startAt,
    endAt: item.endAt,
    lifecycleStatus: item.lifecycleStatus,
    paymentStatus: item.paymentStatus,
    priceSnapshotCents: item.priceSnapshotCents,
    priceSource: ['social', 'social_override'].includes(item.priceSource) ? 'social' : 'standard',
    amountPaidCents: item.amountPaidCents,
    amountRefundedCents: item.amountRefundedCents,
    balanceCents: item.balanceCents,
    expectedPaymentAt: item.expectedPaymentAt ?? '',
    telehealthRoomUrl: item.telehealthRoomUrl ?? '',
    emailSentAt: item.emailSentAt ?? '',
    color: item.appointmentTypeSnapshot.calendarColor,
    events: [],
  };
}

function appointmentEventFromWire(
  id: string,
  event: z.infer<typeof appointmentEventDocumentSchema>,
  actorName: string,
): Appointment['events'][number] {
  const type = event.eventType.includes('pending_scheduling_confirmation')
    ? ('pending_scheduling_confirmation' as const)
    : event.eventType.includes('cancel')
      ? ('cancelled' as const)
      : event.eventType.includes('reschedul')
        ? ('rescheduled' as const)
        : event.eventType.includes('no_show')
          ? ('no_show' as const)
          : event.eventType.includes('complete')
            ? ('completed' as const)
            : event.eventType.includes('confirm')
              ? ('confirmed' as const)
              : ('scheduled' as const);
  const title = event.eventType.includes('cancel')
    ? 'Consulta cancelada'
    : event.eventType.includes('reschedul')
      ? 'Consulta reagendada'
      : event.eventType.includes('no_show')
        ? 'Paciente faltou'
        : 'Situação da consulta atualizada';
  return {
    id,
    type,
    title,
    occurredAt: event.occurredAt,
    actorName,
    detail: 'Evento operacional preservado no histórico.',
  };
}

function idempotencyFingerprint(operation: string, payload: unknown): string {
  return `${operation}:${JSON.stringify(payload)}`;
}

function conversationSummaryFromWire(
  id: string,
  item: z.infer<typeof conversationDocumentSchema>,
): ConversationSummary {
  return {
    id,
    title: item.title ?? (item.type === 'clinic' ? 'Operação do consultório' : 'Conversa direta'),
    type: item.type,
    participantIds: item.participantIds,
    lastMessageAt: item.lastMessageAt ?? '',
    lastMessagePreview: item.lastMessagePreview,
  };
}

interface FinanceMonthWire {
  month: number;
  forecastCents: number;
  grossPaymentsCents: number;
  refundsCents: number;
  netReceiptsCents: number;
}

export function buildFinanceMonths(months: readonly FinanceMonthWire[]): FinanceMonth[] {
  const monthByNumber = new Map(months.map((month) => [month.month, month]));
  return Array.from({ length: 12 }, (_, monthIndex) => {
    const monthNumber = monthIndex + 1;
    const source = monthByNumber.get(monthNumber);
    return {
      month: new Intl.DateTimeFormat('pt-BR', {
        month: 'short',
        timeZone: 'America/Sao_Paulo',
      })
        .format(new Date(Date.UTC(2024, monthIndex, 1, 12)))
        .replace('.', ''),
      forecastCents: source?.forecastCents ?? 0,
      grossReceivedCents: source?.grossPaymentsCents ?? 0,
      refundCents: source?.refundsCents ?? 0,
      netReceivedCents: source?.netReceiptsCents ?? 0,
    };
  });
}

export class FirebaseClinicRepository {
  private readonly pendingIdempotencyKeys = new Map<string, string>();
  private readonly messageCursors = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  private readonly attachmentObjectUrls = new Map<string, string>();
  private users: ClinicUser[] = [];
  private primaryProviderId = '';

  constructor(private readonly session: ClinicUser) {}

  dispose(): void {
    for (const objectUrl of this.attachmentObjectUrls.values()) URL.revokeObjectURL(objectUrl);
    this.attachmentObjectUrls.clear();
  }

  private async loadPatientsById(patientIds: readonly string[]): Promise<Map<string, Patient>> {
    const uniqueIds = [...new Set(patientIds.filter(Boolean))];
    const patients = new Map<string, Patient>();
    const { db } = getFirebaseServices();
    const root = `clinics/${this.session.clinicId}`;
    for (let offset = 0; offset < uniqueIds.length; offset += 25) {
      const chunk = uniqueIds.slice(offset, offset + 25);
      const snapshots = await Promise.all(
        chunk.map((patientId) => getDoc(doc(db, `${root}/patients/${patientId}`))),
      );
      snapshots.forEach((snapshot, index) => {
        if (!snapshot.exists()) return;
        const wire = parseRemote(patientDocumentSchema, normalizeFirestoreValue(snapshot.data()));
        patients.set(chunk[index]!, patientFromWire(chunk[index]!, wire));
      });
    }
    return patients;
  }

  private async mapAppointmentDocuments(
    documents: Array<{ id: string; data: z.infer<typeof appointmentDocumentSchema> }>,
  ): Promise<Appointment[]> {
    const patients = await this.loadPatientsById(documents.map(({ data }) => data.patientId));
    return documents.map(({ id, data }) =>
      appointmentFromWire(id, data, patients.get(data.patientId)?.fullName ?? 'Paciente'),
    );
  }

  private async loadAppointmentDocumentsById(
    appointmentIds: readonly string[],
  ): Promise<Map<string, z.infer<typeof appointmentDocumentSchema>>> {
    const uniqueIds = [...new Set(appointmentIds.filter(Boolean))];
    const appointments = new Map<string, z.infer<typeof appointmentDocumentSchema>>();
    const { db } = getFirebaseServices();
    const root = `clinics/${this.session.clinicId}`;
    for (let offset = 0; offset < uniqueIds.length; offset += 25) {
      const chunk = uniqueIds.slice(offset, offset + 25);
      const snapshots = await Promise.all(
        chunk.map((appointmentId) => getDoc(doc(db, `${root}/appointments/${appointmentId}`))),
      );
      snapshots.forEach((snapshot, index) => {
        if (!snapshot.exists()) return;
        appointments.set(
          chunk[index]!,
          parseRemote(appointmentDocumentSchema, normalizeFirestoreValue(snapshot.data())),
        );
      });
    }
    return appointments;
  }

  private async toChatMessage(
    messageId: string,
    message: z.infer<typeof messageDocumentSchema>,
  ): Promise<Conversation['messages'][number]> {
    const rawAttachment = message.attachment;
    let attachment: ChatAttachment | null = null;
    if (rawAttachment) {
      const storagePath = rawAttachment.storagePath;
      let downloadUrl = '';
      if (storagePath) {
        try {
          const cached = this.attachmentObjectUrls.get(storagePath);
          if (cached) downloadUrl = cached;
          else {
            const blob = await getBlob(ref(getFirebaseServices().storage, storagePath));
            downloadUrl = URL.createObjectURL(blob);
            this.attachmentObjectUrls.set(storagePath, downloadUrl);
          }
        } catch {
          downloadUrl = '';
        }
      }
      attachment = {
        id: rawAttachment.reservationId ?? rawAttachment.attachmentId ?? messageId,
        name:
          rawAttachment.originalFileName ?? rawAttachment.fileName ?? rawAttachment.name ?? 'Anexo',
        mimeType: rawAttachment.contentType ?? rawAttachment.mimeType ?? 'application/octet-stream',
        size: rawAttachment.sizeBytes ?? rawAttachment.size ?? 0,
        url: downloadUrl,
      };
    }
    return {
      id: messageId,
      senderId: message.senderId,
      senderName:
        message.senderName ??
        this.users.find((user) => user.id === message.senderId)?.displayName ??
        'Profissional',
      text: message.text,
      sentAt: message.sentAt,
      status: message.deliveryState ?? message.status,
      attachment,
      reference: message.reference ?? null,
    };
  }

  private async withIdempotency<T>(
    operation: string,
    payload: unknown,
    command: (key: string) => Promise<T>,
  ): Promise<T> {
    const fingerprint = idempotencyFingerprint(operation, payload);
    const key = this.pendingIdempotencyKeys.get(fingerprint) ?? crypto.randomUUID();
    this.pendingIdempotencyKeys.set(fingerprint, key);
    const result = await command(key);
    this.pendingIdempotencyKeys.delete(fingerprint);
    return result;
  }

  async load(): Promise<ClinicData> {
    const root = `clinics/${this.session.clinicId}`;
    const clinicFilter = where('clinicId', '==', this.session.clinicId);
    const windowStart = new Date();
    windowStart.setDate(windowStart.getDate() - 120);
    const windowEnd = new Date();
    windowEnd.setDate(windowEnd.getDate() + 365);
    const [
      memberDocs,
      patientDocs,
      typeDocs,
      availabilityDocs,
      appointmentDocs,
      blockDocs,
      assignedTasks,
      createdTasks,
      directConversations,
      clinicConversations,
      notificationDocs,
    ] = await Promise.all([
      listDocuments(
        `${root}/members`,
        [clinicFilter, where('active', '==', true), limit(100)],
        memberDocumentSchema,
      ),
      listDocuments(
        `${root}/patients`,
        [clinicFilter, orderBy('fullName'), limit(40)],
        patientDocumentSchema,
      ),
      listDocuments(
        `${root}/appointmentTypes`,
        [clinicFilter, orderBy('name'), limit(50)],
        appointmentTypeDocumentSchema,
      ),
      listDocuments(
        `${root}/availabilityRules`,
        [clinicFilter, limit(100)],
        availabilityRuleDocumentSchema,
      ),
      listDocumentsPaginated(
        `${root}/appointments`,
        [
          clinicFilter,
          where('startAt', '>=', windowStart),
          where('startAt', '<=', windowEnd),
          orderBy('startAt'),
        ],
        appointmentDocumentSchema,
        1000,
      ),
      listDocumentsPaginated(
        `${root}/scheduleBlocks`,
        [
          clinicFilter,
          where('startAt', '>=', windowStart),
          where('startAt', '<=', windowEnd),
          orderBy('startAt'),
        ],
        blockDocumentSchema,
        500,
      ),
      listDocuments(
        `${root}/tasks`,
        [
          clinicFilter,
          where('assigneeId', '==', this.session.id),
          orderBy('updatedAt', 'desc'),
          limit(100),
        ],
        taskDocumentSchema,
      ),
      listDocuments(
        `${root}/tasks`,
        [
          clinicFilter,
          where('creatorId', '==', this.session.id),
          orderBy('updatedAt', 'desc'),
          limit(100),
        ],
        taskDocumentSchema,
      ),
      listDocuments(
        `${root}/conversations`,
        [
          clinicFilter,
          where('type', '==', 'direct'),
          where('active', '==', true),
          where('participantIds', 'array-contains', this.session.id),
          limit(50),
        ],
        conversationDocumentSchema,
      ),
      listDocuments(
        `${root}/conversations`,
        [clinicFilter, where('type', '==', 'clinic'), where('active', '==', true), limit(20)],
        conversationDocumentSchema,
      ),
      listDocuments(
        `${root}/notifications`,
        [
          clinicFilter,
          where('recipientId', '==', this.session.id),
          orderBy('createdAt', 'desc'),
          limit(50),
        ],
        notificationDocumentSchema,
      ),
    ]);

    const users: ClinicUser[] = memberDocs.map(({ id, data: member }) => ({
      id,
      clinicId: member.clinicId,
      displayName: member.displayName,
      email: member.email,
      role: member.role,
      avatarColor:
        member.avatarColor ??
        (member.role === 'medico' ? tokens.color.doctorAvatar : tokens.color.secretaryAvatar),
      ...(member.photoPath ? { photoPath: member.photoPath } : {}),
    }));
    this.users = users;
    this.primaryProviderId = selectPrimaryProviderId(users, this.session);
    const patients = patientDocs.map(({ id, data }) => patientFromWire(id, data));
    const patientNames = new Map(patients.map((patient) => [patient.id, patient.fullName]));
    const appointmentTypes: AppointmentType[] = typeDocs.map(({ id, data: type }) => ({
      id,
      name: type.name,
      durationMinutes: type.durationMinutes,
      defaultPriceCents: type.defaultPriceCents,
      calendarColor: type.calendarColor,
      active: type.active,
    }));
    const availabilityRules: AvailabilityRule[] = availabilityDocs.map(({ id, data: rule }) => ({
      id,
      providerId: rule.providerId,
      weekday: rule.weekday,
      startMinute: rule.startMinute,
      endMinute: rule.endMinute,
      validFromLocalDate: rule.validFromLocalDate,
      validUntilLocalDate: rule.validUntilLocalDate ?? '',
      active: rule.active,
    }));
    const appointments: Appointment[] = appointmentDocs.map(({ id, data: item }) =>
      appointmentFromWire(id, item, patientNames.get(item.patientId) ?? 'Paciente'),
    );
    const scheduleBlocks: ScheduleBlock[] = blockDocs.map(({ id, data: block }) => ({
      id,
      startAt: block.startAt,
      endAt: block.endAt,
      reason: block.reason,
      status: block.status,
      createdBy: block.createdBy,
      updatedBy: block.updatedBy,
    }));
    const taskById = new Map([...assignedTasks, ...createdTasks].map((item) => [item.id, item]));
    const tasks: ClinicTask[] = [...taskById.values()].map(({ id, data: task }) => ({
      id,
      title: task.title,
      description: task.description,
      creatorId: task.creatorId,
      creatorName: users.find((user) => user.id === task.creatorId)?.displayName ?? 'Profissional',
      assigneeId: task.assigneeId,
      assigneeName:
        users.find((user) => user.id === task.assigneeId)?.displayName ?? 'Profissional',
      status: task.status,
      priority: task.priority,
      dueAt: task.dueAt ?? '',
      createdAt: task.createdAt,
      updatedAt: task.updatedAt,
      completedAt: task.completedAt ?? '',
      patientId: task.patientId ?? '',
      appointmentId: task.appointmentId ?? '',
      activity: [],
    }));
    const conversationById = new Map(
      [...directConversations, ...clinicConversations].map((item) => [item.id, item]),
    );
    const { db } = getFirebaseServices();
    const conversations: Conversation[] = await Promise.all(
      [...conversationById.values()].map(async ({ id, data: item }) => {
        const [messageDocs, readState] = await Promise.all([
          listDocuments(
            `${root}/conversations/${id}/messages`,
            [clinicFilter, where('conversationId', '==', id), orderBy('sentAt', 'desc'), limit(50)],
            messageDocumentSchema,
          ),
          getDoc(doc(db, `${root}/conversations/${id}/readStates/${this.session.id}`)),
        ]);
        const messages = await Promise.all(
          messageDocs
            .reverse()
            .map(({ id: messageId, data: message }) => this.toChatMessage(messageId, message)),
        );
        const unreadCount = readState.exists()
          ? parseRemote(
              conversationReadStateDocumentSchema,
              normalizeFirestoreValue(readState.data()),
            ).unreadCount
          : 0;
        return {
          ...conversationSummaryFromWire(id, item),
          lastMessageAt: item.lastMessageAt ?? messages.at(-1)?.sentAt ?? '',
          lastMessagePreview:
            item.lastMessagePreview ||
            messages.at(-1)?.text ||
            (messages.at(-1)?.attachment ? 'Anexo' : ''),
          unreadByUser: { [this.session.id]: unreadCount },
          messages,
        };
      }),
    );
    const notifications: ClinicNotification[] = notificationDocs.map(({ id, data: item }) => {
      const mappedType: ClinicNotification['type'] = item.type.includes('chat')
        ? 'chat'
        : item.type.includes('payment')
          ? 'payment'
          : item.type.includes('appointment')
            ? 'appointment'
            : 'task';
      const titleByType = {
        chat: 'Nova mensagem',
        payment: 'Atualização de pagamento',
        appointment: 'Atualização da agenda',
        task: 'Atualização de tarefa',
      } as const;
      const hrefByType = {
        chat: '/conversas',
        payment: '/financeiro',
        appointment: '/agenda',
        task: '/tarefas',
      } as const;
      return {
        id,
        type: mappedType,
        title: titleByType[mappedType],
        detail: item.message,
        createdAt: item.createdAt,
        read: item.readAt != null,
        href: hrefByType[mappedType],
      };
    });

    let financeMonths: FinanceMonth[] = [];
    if (this.session.role === 'medico') {
      const financeSchema = z
        .object({
          months: z.array(
            z.object({
              month: z.number().int().min(1).max(12),
              forecastCents: z.number().int(),
              grossPaymentsCents: z.number().int(),
              refundsCents: z.number().int(),
              netReceiptsCents: z.number().int(),
            }),
          ),
        })
        .passthrough();
      const response = await invoke(
        'getDoctorFinanceSummary',
        { year: new Date().getFullYear() },
        financeSchema,
      );
      financeMonths = buildFinanceMonths(response.months);
    }

    return {
      users,
      patients,
      appointmentTypes,
      availabilityRules,
      appointments,
      scheduleBlocks,
      tasks,
      conversations,
      notifications,
      financeMonths,
    };
  }

  subscribeToOperationalData(
    onPatch: (patch: Partial<ClinicData>) => void,
    onRealtimeUpdate: (message: string) => void,
    onError: (message: string) => void,
  ): Unsubscribe {
    const { db } = getFirebaseServices();
    const root = `clinics/${this.session.clinicId}`;
    const clinicFilter = where('clinicId', '==', this.session.clinicId);
    const unsubscribers: Unsubscribe[] = [];
    let assignedTaskDocuments: Array<{
      id: string;
      data: z.infer<typeof taskDocumentSchema>;
    }> = [];
    let createdTaskDocuments: Array<{
      id: string;
      data: z.infer<typeof taskDocumentSchema>;
    }> = [];

    const mapTasks = (): ClinicTask[] => {
      const taskById = new Map(
        [...assignedTaskDocuments, ...createdTaskDocuments].map((item) => [item.id, item]),
      );
      return [...taskById.values()].map(({ id, data: task }) => ({
        id,
        title: task.title,
        description: task.description,
        creatorId: task.creatorId,
        creatorName:
          this.users.find((user) => user.id === task.creatorId)?.displayName ?? 'Profissional',
        assigneeId: task.assigneeId,
        assigneeName:
          this.users.find((user) => user.id === task.assigneeId)?.displayName ?? 'Profissional',
        status: task.status,
        priority: task.priority,
        dueAt: task.dueAt ?? '',
        createdAt: task.createdAt,
        updatedAt: task.updatedAt,
        completedAt: task.completedAt ?? '',
        patientId: task.patientId ?? '',
        appointmentId: task.appointmentId ?? '',
        activity: [],
      }));
    };

    const watch = <T>(
      path: string,
      constraints: QueryConstraint[],
      schema: z.ZodType<T>,
      transform: (
        documents: Array<{ id: string; data: T }>,
      ) => Partial<ClinicData> | Promise<Partial<ClinicData>>,
      updateMessage: string,
    ) => {
      let initialized = false;
      let snapshotVersion = 0;
      const unsubscribe = onSnapshot(
        query(collection(db, path), ...constraints),
        (snapshot) => {
          const version = snapshotVersion + 1;
          snapshotVersion = version;
          const hasExternalChange =
            initialized &&
            snapshot
              .docChanges()
              .some((change) => wasChangedByExternalActor(change.doc.data(), this.session.id));
          initialized = true;
          try {
            const documents = snapshot.docs.map((document) => ({
              id: document.id,
              data: parseRemote(schema, normalizeFirestoreValue(document.data())),
            }));
            void Promise.resolve(transform(documents))
              .then((patch) => {
                if (version !== snapshotVersion) return;
                onPatch(patch);
                if (hasExternalChange) onRealtimeUpdate(updateMessage);
              })
              .catch(() =>
                onError('Não foi possível aplicar uma atualização recebida em tempo real.'),
              );
          } catch {
            onError(
              'Uma atualização recebida está incompleta. Atualize a página ou fale com o suporte.',
            );
          }
        },
        (caught) =>
          onError(
            toPortugueseError(caught, 'Não foi possível manter os dados atualizados em tempo real.')
              .message,
          ),
      );
      unsubscribers.push(unsubscribe);
    };

    watch(
      `${root}/members`,
      [clinicFilter, where('active', '==', true), limit(100)],
      memberDocumentSchema,
      (documents) => {
        const users: ClinicUser[] = documents.map(({ id, data: member }) => ({
          id,
          clinicId: member.clinicId,
          displayName: member.displayName,
          email: member.email,
          role: member.role,
          avatarColor:
            member.avatarColor ??
            (member.role === 'medico' ? tokens.color.doctorAvatar : tokens.color.secretaryAvatar),
          ...(member.photoPath ? { photoPath: member.photoPath } : {}),
        }));
        this.users = users;
        this.primaryProviderId = selectPrimaryProviderId(users, this.session);
        return { users, tasks: mapTasks() };
      },
      'A equipe do consultório foi atualizada.',
    );

    watch(
      `${root}/patients`,
      [clinicFilter, orderBy('fullName'), limit(40)],
      patientDocumentSchema,
      (documents) => ({
        patients: documents.map(({ id, data }) => patientFromWire(id, data)),
      }),
      'A lista de pacientes foi atualizada em tempo real.',
    );

    watch(
      `${root}/appointmentTypes`,
      [clinicFilter, orderBy('name'), limit(50)],
      appointmentTypeDocumentSchema,
      (documents) => ({
        appointmentTypes: documents.map(({ id, data: type }) => ({
          id,
          name: type.name,
          durationMinutes: type.durationMinutes,
          defaultPriceCents: type.defaultPriceCents,
          calendarColor: type.calendarColor,
          active: type.active,
        })),
      }),
      'Os tipos de atendimento foram atualizados em tempo real.',
    );

    watch(
      `${root}/availabilityRules`,
      [clinicFilter, limit(100)],
      availabilityRuleDocumentSchema,
      (documents) => ({
        availabilityRules: documents.map(({ id, data: rule }) => ({
          id,
          providerId: rule.providerId,
          weekday: rule.weekday,
          startMinute: rule.startMinute,
          endMinute: rule.endMinute,
          validFromLocalDate: rule.validFromLocalDate,
          validUntilLocalDate: rule.validUntilLocalDate ?? '',
          active: rule.active,
        })),
      }),
      'A disponibilidade da agenda foi atualizada em tempo real.',
    );

    watch(
      `${root}/tasks`,
      [
        clinicFilter,
        where('assigneeId', '==', this.session.id),
        orderBy('updatedAt', 'desc'),
        limit(100),
      ],
      taskDocumentSchema,
      (documents) => {
        assignedTaskDocuments = documents;
        return { tasks: mapTasks() };
      },
      'Suas tarefas foram atualizadas em tempo real.',
    );

    watch(
      `${root}/tasks`,
      [
        clinicFilter,
        where('creatorId', '==', this.session.id),
        orderBy('updatedAt', 'desc'),
        limit(100),
      ],
      taskDocumentSchema,
      (documents) => {
        createdTaskDocuments = documents;
        return { tasks: mapTasks() };
      },
      'As tarefas criadas por você foram atualizadas em tempo real.',
    );

    watch(
      `${root}/notifications`,
      [
        clinicFilter,
        where('recipientId', '==', this.session.id),
        orderBy('createdAt', 'desc'),
        limit(50),
      ],
      notificationDocumentSchema,
      (documents) => ({
        notifications: documents.map(({ id, data: item }) => {
          const mappedType: ClinicNotification['type'] = item.type.includes('chat')
            ? 'chat'
            : item.type.includes('payment')
              ? 'payment'
              : item.type.includes('appointment')
                ? 'appointment'
                : 'task';
          const titleByType = {
            chat: 'Nova mensagem',
            payment: 'Atualização de pagamento',
            appointment: 'Atualização da agenda',
            task: 'Atualização de tarefa',
          } as const;
          const hrefByType = {
            chat: '/conversas',
            payment: '/financeiro',
            appointment: '/agenda',
            task: '/tarefas',
          } as const;
          return {
            id,
            type: mappedType,
            title: titleByType[mappedType],
            detail: item.message,
            createdAt: item.createdAt,
            read: item.readAt != null,
            href: hrefByType[mappedType],
          };
        }),
      }),
      'Você recebeu uma nova atualização.',
    );

    if (this.session.role === 'medico') {
      const currentYear = new Date().getFullYear();
      watch(
        `${root}/financePrivate`,
        [clinicFilter, limit(24)],
        financePrivateDocumentSchema,
        (documents) => ({
          financeMonths: buildFinanceMonths(
            documents
              .filter(({ data: month }) => month.year === currentYear)
              .map(({ data: month }) => month),
          ),
        }),
        'Os indicadores financeiros foram atualizados em tempo real.',
      );
    }

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }

  subscribeToScheduleRange(
    rangeStartAt: string,
    rangeEndAt: string,
    onData: (result: ScheduleRangeResult) => void,
    onRealtimeUpdate: (message: string) => void,
    onError: (message: string) => void,
  ): Unsubscribe {
    const rangeStart = new Date(rangeStartAt);
    const rangeEnd = new Date(rangeEndAt);
    if (
      Number.isNaN(rangeStart.getTime()) ||
      Number.isNaN(rangeEnd.getTime()) ||
      rangeEnd <= rangeStart
    ) {
      queueMicrotask(() => onError('O período solicitado para a agenda é inválido.'));
      return () => undefined;
    }

    const { db } = getFirebaseServices();
    const root = `clinics/${this.session.clinicId}`;
    const clinicFilter = where('clinicId', '==', this.session.clinicId);
    const chunks: Array<{ start: Date; end: Date }> = [];
    for (let cursor = rangeStart.getTime(); cursor < rangeEnd.getTime(); cursor += 7 * 86_400_000) {
      chunks.push({
        start: new Date(cursor),
        end: new Date(Math.min(cursor + 7 * 86_400_000, rangeEnd.getTime())),
      });
    }

    const appointmentDocumentsByChunk = new Map<
      number,
      Array<{ id: string; data: z.infer<typeof appointmentDocumentSchema> }>
    >();
    const blockDocumentsByChunk = new Map<
      number,
      Array<{ id: string; data: z.infer<typeof blockDocumentSchema> }>
    >();
    const appointmentInitialized = new Set<number>();
    const blockInitialized = new Set<number>();
    const unsubscribers: Unsubscribe[] = [];
    let emitVersion = 0;

    const emit = () => {
      if (
        appointmentDocumentsByChunk.size !== chunks.length ||
        blockDocumentsByChunk.size !== chunks.length
      ) {
        return;
      }
      const version = emitVersion + 1;
      emitVersion = version;
      const appointmentById = new Map(
        [...appointmentDocumentsByChunk.values()].flat().map((item) => [item.id, item]),
      );
      const blockById = new Map(
        [...blockDocumentsByChunk.values()].flat().map((item) => [item.id, item]),
      );
      void this.mapAppointmentDocuments([...appointmentById.values()])
        .then((appointments) => {
          if (version !== emitVersion) return;
          onData({
            appointments,
            scheduleBlocks: [...blockById.values()].map(({ id, data: block }) => ({
              id,
              startAt: block.startAt,
              endAt: block.endAt,
              reason: block.reason,
              status: block.status,
              createdBy: block.createdBy,
              updatedBy: block.updatedBy,
            })),
          });
        })
        .catch(() => onError('Não foi possível aplicar a atualização da agenda.'));
    };

    chunks.forEach((chunk, index) => {
      const appointmentUnsubscribe = onSnapshot(
        query(
          collection(db, `${root}/appointments`),
          clinicFilter,
          where('startAt', '<', chunk.end),
          where('endAt', '>', chunk.start),
          orderBy('startAt'),
          orderBy('endAt'),
          limit(100),
        ),
        (snapshot) => {
          if (snapshot.size === 100) {
            onError(
              'Há consultas demais neste período para acompanhar em tempo real. Reduza a visualização.',
            );
            return;
          }
          const notify =
            appointmentInitialized.has(index) &&
            snapshot
              .docChanges()
              .some((change) => wasChangedByExternalActor(change.doc.data(), this.session.id));
          appointmentInitialized.add(index);
          appointmentDocumentsByChunk.set(
            index,
            snapshot.docs.map((document) => ({
              id: document.id,
              data: parseRemote(
                appointmentDocumentSchema,
                normalizeFirestoreValue(document.data()),
              ),
            })),
          );
          emit();
          if (notify) onRealtimeUpdate('A agenda foi atualizada em tempo real.');
        },
        (caught) =>
          onError(
            toPortugueseError(caught, 'Não foi possível acompanhar a agenda em tempo real.')
              .message,
          ),
      );
      unsubscribers.push(appointmentUnsubscribe);

      const blockUnsubscribe = onSnapshot(
        query(
          collection(db, `${root}/scheduleBlocks`),
          clinicFilter,
          where('status', '==', 'active'),
          where('startAt', '<', chunk.end),
          where('endAt', '>', chunk.start),
          orderBy('startAt'),
          orderBy('endAt'),
          limit(100),
        ),
        (snapshot) => {
          if (snapshot.size === 100) {
            onError(
              'Há bloqueios demais neste período para acompanhar em tempo real. Reduza a visualização.',
            );
            return;
          }
          const notify =
            blockInitialized.has(index) &&
            snapshot
              .docChanges()
              .some((change) => wasChangedByExternalActor(change.doc.data(), this.session.id));
          blockInitialized.add(index);
          blockDocumentsByChunk.set(
            index,
            snapshot.docs.map((document) => ({
              id: document.id,
              data: parseRemote(blockDocumentSchema, normalizeFirestoreValue(document.data())),
            })),
          );
          emit();
          if (notify) onRealtimeUpdate('Os bloqueios da agenda foram atualizados em tempo real.');
        },
        (caught) =>
          onError(
            toPortugueseError(caught, 'Não foi possível acompanhar os bloqueios em tempo real.')
              .message,
          ),
      );
      unsubscribers.push(blockUnsubscribe);
    });

    return () => {
      emitVersion += 1;
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }

  async searchPatients(
    queryText: string,
    offset = 0,
    activeOnly = false,
  ): Promise<{ items: PatientSearchResult[]; nextOffset: number | null }> {
    return invoke(
      'searchPatients',
      { query: queryText, offset, limit: 12, activeOnly },
      searchResultSchema,
    );
  }

  async loadPatientsPage(cursor: PatientListCursor | null = null): Promise<PatientListPage> {
    const { db } = getFirebaseServices();
    const root = `clinics/${this.session.clinicId}`;
    const pageSize = 24;
    const constraints: QueryConstraint[] = [
      where('clinicId', '==', this.session.clinicId),
      orderBy('fullName'),
      orderBy(documentId()),
    ];
    if (cursor) constraints.push(startAfter(cursor.fullName, cursor.id));
    constraints.push(limit(pageSize + 1));

    const snapshot = await getDocs(query(collection(db, `${root}/patients`), ...constraints));
    const hasMore = snapshot.docs.length > pageSize;
    const pageDocuments = snapshot.docs.slice(0, pageSize);
    const last = pageDocuments.at(-1);
    return {
      items: pageDocuments.map((document) =>
        patientFromWire(
          document.id,
          parseRemote(patientDocumentSchema, normalizeFirestoreValue(document.data())),
        ),
      ),
      nextCursor:
        hasMore && last
          ? {
              fullName: String(last.data().fullName),
              id: last.id,
            }
          : null,
    };
  }

  async loadPatientForScheduling(patientId: string): Promise<Patient> {
    const patient = (await this.loadPatientsById([patientId])).get(patientId);
    if (!patient?.active) throw new Error('Paciente não encontrado ou inativo. Atualize a busca.');
    return patient;
  }

  async loadScheduleRange(rangeStartAt: string, rangeEndAt: string): Promise<ScheduleRangeResult> {
    const rangeStart = new Date(rangeStartAt);
    const rangeEnd = new Date(rangeEndAt);
    if (
      Number.isNaN(rangeStart.getTime()) ||
      Number.isNaN(rangeEnd.getTime()) ||
      rangeEnd <= rangeStart
    ) {
      throw new Error('O período solicitado para a agenda é inválido.');
    }
    const root = `clinics/${this.session.clinicId}`;
    const clinicFilter = where('clinicId', '==', this.session.clinicId);
    const [appointmentDocuments, blockDocuments] = await Promise.all([
      listDocumentsPaginated(
        `${root}/appointments`,
        [
          clinicFilter,
          where('startAt', '<', rangeEnd),
          where('endAt', '>', rangeStart),
          orderBy('startAt'),
          orderBy('endAt'),
        ],
        appointmentDocumentSchema,
        5000,
      ),
      listDocumentsPaginated(
        `${root}/scheduleBlocks`,
        [
          clinicFilter,
          where('status', '==', 'active'),
          where('startAt', '<', rangeEnd),
          where('endAt', '>', rangeStart),
          orderBy('startAt'),
          orderBy('endAt'),
        ],
        blockDocumentSchema,
        2000,
      ),
    ]);
    return {
      appointments: await this.mapAppointmentDocuments(appointmentDocuments),
      scheduleBlocks: blockDocuments.map(({ id, data: block }) => ({
        id,
        startAt: block.startAt,
        endAt: block.endAt,
        reason: block.reason,
        status: block.status,
        createdBy: block.createdBy,
        updatedBy: block.updatedBy,
      })),
    };
  }

  async loadOutstandingAppointments(
    cursor: OutstandingAppointmentsCursor | null = null,
  ): Promise<OutstandingAppointmentsPage> {
    const { db } = getFirebaseServices();
    const root = `clinics/${this.session.clinicId}`;
    const pageSize = 24;
    const constraints: QueryConstraint[] = [
      where('clinicId', '==', this.session.clinicId),
      where('balanceCents', '>', 0),
      where('endAt', '<', new Date()),
      orderBy('balanceCents', 'desc'),
      orderBy('endAt', 'desc'),
      orderBy(documentId(), 'desc'),
    ];
    if (cursor) {
      constraints.push(startAfter(cursor.balanceCents, new Date(cursor.endAt), cursor.id));
    }
    constraints.push(limit(pageSize + 1));
    const snapshot = await getDocs(query(collection(db, `${root}/appointments`), ...constraints));
    const hasMore = snapshot.docs.length > pageSize;
    const pageDocuments = snapshot.docs.slice(0, pageSize).map((document) => ({
      id: document.id,
      data: parseRemote(appointmentDocumentSchema, normalizeFirestoreValue(document.data())),
    }));
    const last = pageDocuments.at(-1);
    return {
      items: (await this.mapAppointmentDocuments(pageDocuments)).filter((appointment) =>
        isOutstandingCompletedAppointment(appointment),
      ),
      nextCursor:
        hasMore && last
          ? { balanceCents: last.data.balanceCents, endAt: last.data.endAt, id: last.id }
          : null,
    };
  }

  async loadDashboardOperationalData(
    scheduleStartAt: string,
    scheduleEndAt: string,
    outstandingReferenceAt: string,
  ): Promise<DashboardOperationalData> {
    const scheduleStart = new Date(scheduleStartAt);
    const scheduleEnd = new Date(scheduleEndAt);
    const outstandingReference = new Date(outstandingReferenceAt);
    if (
      [scheduleStart, scheduleEnd, outstandingReference].some((date) =>
        Number.isNaN(date.getTime()),
      ) ||
      scheduleEnd <= scheduleStart
    ) {
      throw new Error('Não foi possível definir o período operacional do painel.');
    }
    const root = `clinics/${this.session.clinicId}`;
    const clinicFilter = where('clinicId', '==', this.session.clinicId);
    const [
      appointmentDocuments,
      advanceDocuments,
      completedOutstandingDocuments,
      elapsedOutstandingDocuments,
    ] = await Promise.all([
      listDocumentsPaginated(
        `${root}/appointments`,
        [
          clinicFilter,
          where('startAt', '>=', scheduleStart),
          where('startAt', '<', scheduleEnd),
          orderBy('startAt'),
        ],
        appointmentDocumentSchema,
        1000,
      ),
      listDocuments(
        `${root}/patients`,
        [clinicFilter, where('requiresAdvancePayment', '==', true), orderBy('fullName'), limit(21)],
        patientDocumentSchema,
      ),
      listDocuments(
        `${root}/appointments`,
        [
          clinicFilter,
          where('lifecycleStatus', '==', 'completed'),
          where('balanceCents', '>', 0),
          orderBy('balanceCents', 'desc'),
          orderBy('endAt', 'desc'),
          limit(21),
        ],
        appointmentDocumentSchema,
      ),
      listDocuments(
        `${root}/appointments`,
        [
          clinicFilter,
          where('lifecycleStatus', 'in', [
            'pending_scheduling_confirmation',
            'scheduled',
            'confirmed',
          ]),
          where('balanceCents', '>', 0),
          where('endAt', '<=', outstandingReference),
          orderBy('balanceCents', 'desc'),
          orderBy('endAt', 'desc'),
          limit(21),
        ],
        appointmentDocumentSchema,
      ),
    ]);
    const outstandingDocuments = [
      ...new Map(
        [...completedOutstandingDocuments, ...elapsedOutstandingDocuments].map((document) => [
          document.id,
          document,
        ]),
      ).values(),
    ];
    const appointmentPatients = await this.loadPatientsById(
      [...appointmentDocuments, ...outstandingDocuments].map(({ data }) => data.patientId),
    );
    const mapAppointment = ({
      id,
      data,
    }: {
      id: string;
      data: z.infer<typeof appointmentDocumentSchema>;
    }) =>
      appointmentFromWire(
        id,
        data,
        appointmentPatients.get(data.patientId)?.fullName ?? 'Paciente',
      );
    const outstandingAppointments = outstandingDocuments
      .map(mapAppointment)
      .filter((appointment) =>
        isOutstandingCompletedAppointment(appointment, outstandingReferenceAt),
      )
      .sort(
        (left, right) =>
          right.balanceCents - left.balanceCents || right.endAt.localeCompare(left.endAt),
      );
    return {
      appointments: appointmentDocuments.map(mapAppointment),
      advancePaymentPatients: advanceDocuments
        .slice(0, 20)
        .map(({ id, data }) => patientFromWire(id, data)),
      advancePaymentPatientsHasMore: advanceDocuments.length > 20,
      outstandingAppointments: outstandingAppointments.slice(0, 20),
      outstandingAppointmentsHasMore:
        completedOutstandingDocuments.length > 20 ||
        elapsedOutstandingDocuments.length > 20 ||
        outstandingAppointments.length > 20,
    };
  }

  async loadPatientDetails(
    patientId: string,
    cursor: PatientAppointmentCursor | null = null,
  ): Promise<PatientDetailsResult> {
    const root = `clinics/${this.session.clinicId}`;
    const { db } = getFirebaseServices();
    const snapshot = await getDoc(doc(db, `${root}/patients/${patientId}`));
    if (!snapshot.exists()) throw new Error('Paciente não encontrado ou sem permissão de acesso.');
    const patientWire = parseRemote(
      patientDocumentSchema,
      normalizeFirestoreValue(snapshot.data()),
    );
    const patient = patientFromWire(patientId, patientWire);
    const clinicFilter = where('clinicId', '==', this.session.clinicId);
    const appointmentConstraints: QueryConstraint[] = [
      clinicFilter,
      where('patientId', '==', patientId),
      orderBy('startAt', 'desc'),
      orderBy(documentId(), 'desc'),
    ];
    if (cursor) appointmentConstraints.push(startAfter(new Date(cursor.startAt), cursor.id));
    appointmentConstraints.push(limit(26));
    const appointmentSnapshot = await getDocs(
      query(collection(db, `${root}/appointments`), ...appointmentConstraints),
    );
    const hasMoreAppointments = appointmentSnapshot.docs.length > 25;
    const appointmentDocs = appointmentSnapshot.docs.slice(0, 25).map((document) => ({
      id: document.id,
      data: parseRemote(appointmentDocumentSchema, normalizeFirestoreValue(document.data())),
    }));
    const eventPages = await Promise.all(
      appointmentDocs.map(({ id }) =>
        listDocumentsPaginated(
          `${root}/appointments/${id}/events`,
          [clinicFilter, where('appointmentId', '==', id), orderBy('occurredAt', 'desc')],
          appointmentEventDocumentSchema,
          5000,
          50,
          'Esta consulta possui muitos eventos para carregar de uma só vez. Tente novamente ou fale com o suporte.',
        ),
      ),
    );
    const actorName = (actorId: string) =>
      this.users.find((user) => user.id === actorId)?.displayName ?? 'Profissional';
    const appointments = appointmentDocs.map(({ id, data: item }, index) => {
      const appointment = appointmentFromWire(id, item, patient.fullName);
      appointment.events = (eventPages[index] ?? [])
        .map(({ id: eventId, data: event }) =>
          appointmentEventFromWire(eventId, event, actorName(event.actorId)),
        )
        .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
      return appointment;
    });
    const lastAppointment = appointmentDocs.at(-1);
    return {
      patient,
      appointments,
      nextCursor:
        hasMoreAppointments && lastAppointment
          ? { startAt: lastAppointment.data.startAt, id: lastAppointment.id }
          : null,
    };
  }

  async loadPatientAppointments(
    patientId: string,
    cursor: PatientAppointmentCursor,
  ): Promise<PatientAppointmentPage> {
    const result = await this.loadPatientDetails(patientId, cursor);
    return { appointments: result.appointments, nextCursor: result.nextCursor };
  }

  async loadPatientPaymentHistory(
    patientId: string,
    cursor: PatientPaymentCursor | null = null,
  ): Promise<PatientPaymentHistoryPage> {
    const { db } = getFirebaseServices();
    const root = `clinics/${this.session.clinicId}`;
    const constraints: QueryConstraint[] = [
      where('clinicId', '==', this.session.clinicId),
      where('patientId', '==', patientId),
      orderBy('occurredAt', 'desc'),
      orderBy(documentId(), 'desc'),
    ];
    if (cursor) constraints.push(startAfter(new Date(cursor.occurredAt), cursor.id));
    constraints.push(limit(51));
    const snapshot = await getDocs(query(collection(db, `${root}/paymentLedger`), ...constraints));
    const hasMore = snapshot.docs.length > 50;
    const entries = snapshot.docs.slice(0, 50).map((document) => ({
      id: document.id,
      data: parseRemote(paymentLedgerDocumentSchema, normalizeFirestoreValue(document.data())),
    }));
    const appointmentDocuments = await this.loadAppointmentDocumentsById(
      entries.map(({ data }) => data.appointmentId),
    );
    const last = entries.at(-1);
    return {
      items: entries.map(({ id, data: entry }) => {
        const appointment = appointmentDocuments.get(entry.appointmentId);
        return {
          id,
          appointmentId: entry.appointmentId,
          appointmentTypeName: appointment?.appointmentTypeSnapshot.name ?? 'Atendimento',
          appointmentStartAt: appointment?.startAt ?? entry.occurredAt,
          appointmentBalanceCents: appointment?.balanceCents ?? 0,
          entryType: entry.entryType,
          amountCents: entry.amountCents,
          occurredAt: entry.occurredAt,
          actorName:
            this.users.find((user) => user.id === entry.actorId)?.displayName ?? 'Profissional',
        };
      }),
      nextCursor: hasMore && last ? { occurredAt: last.data.occurredAt, id: last.id } : null,
    };
  }

  createPatient(patient: PatientDraft) {
    const wire = patientDraftToWire(patient);
    return this.withIdempotency('patient.create', wire, (idempotencyKey) =>
      invoke('createPatient', { patient: wire, idempotencyKey }, patientMutationResultSchema),
    );
  }

  updatePatient(patientId: string, patient: PatientDraft) {
    const wire = patientDraftToWire(patient);
    return this.withIdempotency(`patient.update:${patientId}`, wire, (idempotencyKey) =>
      invoke(
        'updatePatient',
        { patientId, patient: wire, idempotencyKey },
        patientMutationResultSchema,
      ),
    );
  }

  updatePatientSchedulingAlert(patientId: string, update: PatientSchedulingAlertUpdate) {
    const payload = { patientId, ...update };
    return this.withIdempotency(
      `patient.schedulingAlert.update:${patientId}`,
      payload,
      (idempotencyKey) =>
        invoke(
          'updatePatientSchedulingAlert',
          { ...payload, idempotencyKey },
          patientMutationResultSchema,
        ),
    );
  }

  updatePatientStatus(
    patientId: string,
    status: 'active' | 'inactive',
    inactiveReason: 'medical_discharge' | 'abandonment' | null,
  ) {
    const payload = { patientId, status, inactiveReason };
    return this.withIdempotency(`patient.status.update:${patientId}`, payload, (idempotencyKey) =>
      invoke('updatePatientStatus', { ...payload, idempotencyKey }, patientMutationResultSchema),
    );
  }

  updatePatientStatuses(
    patientIds: string[],
    status: 'active' | 'inactive',
    inactiveReason: 'medical_discharge' | 'abandonment' | null,
  ) {
    const payload = { patientIds: [...patientIds].sort(), status, inactiveReason };
    return this.withIdempotency('patient.status.bulkUpdate', payload, (idempotencyKey) =>
      invoke(
        'updatePatientStatuses',
        { ...payload, idempotencyKey },
        patientBulkMutationResultSchema,
      ),
    );
  }

  createAppointmentType(appointmentType: Omit<AppointmentType, 'id'>) {
    const wire = appointmentTypeInputSchema.parse(appointmentType);
    return this.withIdempotency('appointment-type.create', wire, (idempotencyKey) =>
      invoke(
        'createAppointmentType',
        { appointmentType: wire, idempotencyKey },
        z.object({ appointmentTypeId: idSchema }),
      ),
    );
  }

  updateAppointmentType(appointmentTypeId: string, appointmentType: Omit<AppointmentType, 'id'>) {
    const wire = appointmentTypeInputSchema.parse(appointmentType);
    return this.withIdempotency(
      `appointment-type.update:${appointmentTypeId}`,
      wire,
      (idempotencyKey) =>
        invoke(
          'updateAppointmentType',
          { appointmentTypeId, appointmentType: wire, idempotencyKey },
          z.object({ appointmentTypeId: idSchema }),
        ),
    );
  }

  retireAppointmentType(appointmentTypeId: string) {
    return this.withIdempotency(
      `appointment-type.retire:${appointmentTypeId}`,
      { appointmentTypeId },
      (idempotencyKey) =>
        invoke(
          'retireAppointmentType',
          { appointmentTypeId, idempotencyKey },
          z.object({ appointmentTypeId: idSchema }),
        ),
    );
  }

  createAvailabilityRule(availabilityRule: Omit<AvailabilityRule, 'id'>) {
    return this.withIdempotency('availability-rule.create', availabilityRule, (idempotencyKey) =>
      invoke(
        'createAvailabilityRule',
        {
          availabilityRule: {
            ...availabilityRule,
            validUntilLocalDate: availabilityRule.validUntilLocalDate || null,
          },
          idempotencyKey,
        },
        z.object({ availabilityRuleId: idSchema }),
      ),
    );
  }

  updateAvailabilityRule(
    availabilityRuleId: string,
    availabilityRule: Omit<AvailabilityRule, 'id'>,
  ) {
    return this.withIdempotency(
      `availability-rule.update:${availabilityRuleId}`,
      availabilityRule,
      (idempotencyKey) =>
        invoke(
          'updateAvailabilityRule',
          {
            availabilityRuleId,
            availabilityRule: {
              ...availabilityRule,
              validUntilLocalDate: availabilityRule.validUntilLocalDate || null,
            },
            idempotencyKey,
          },
          z.object({ availabilityRuleId: idSchema }),
        ),
    );
  }

  retireAvailabilityRule(availabilityRuleId: string) {
    return this.withIdempotency(
      `availability-rule.retire:${availabilityRuleId}`,
      { availabilityRuleId },
      (idempotencyKey) =>
        invoke(
          'retireAvailabilityRule',
          { availabilityRuleId, idempotencyKey },
          z.object({ availabilityRuleId: idSchema }),
        ),
    );
  }

  createAppointment(draft: AppointmentDraft) {
    const providerId = this.primaryProviderId || selectPrimaryProviderId(this.users, this.session);
    const payload = {
      patientId: draft.patientId,
      providerId,
      appointmentTypeId: draft.appointmentTypeId,
      startAt: draft.startAt,
      expectedPaymentAt: draft.expectedPaymentAt || null,
    };
    return this.withIdempotency('appointment.create', payload, (idempotencyKey) =>
      invoke('createAppointment', { ...payload, idempotencyKey }, appointmentCreateResultSchema),
    );
  }

  createScheduleBlock(draft: ScheduleBlockDraft, recurrenceDates: string[] = []) {
    const providerId = this.primaryProviderId || selectPrimaryProviderId(this.users, this.session);
    const payload = { ...draft, providerId, recurrenceDates };
    return this.withIdempotency('schedule-block.create', payload, (idempotencyKey) =>
      invoke(
        'createScheduleBlock',
        { ...payload, idempotencyKey },
        z.object({ blockIds: z.array(idSchema) }),
      ),
    );
  }

  transitionAppointment(
    appointmentId: string,
    nextStatus: Appointment['lifecycleStatus'],
    expectedPaymentAt?: string | null,
  ) {
    const payload = { appointmentId, nextStatus, expectedPaymentAt };
    return this.withIdempotency(
      `appointment.lifecycle:${appointmentId}`,
      payload,
      (idempotencyKey) =>
        invoke(
          'transitionAppointmentLifecycle',
          { ...payload, idempotencyKey },
          z.object({ appointmentId: idSchema }).passthrough(),
        ),
    );
  }

  updateScheduleBlock(blockId: string, draft: ScheduleBlockDraft, recurrenceDates: string[] = []) {
    const providerId = this.primaryProviderId || selectPrimaryProviderId(this.users, this.session);
    const payload = { blockId, ...draft, providerId, recurrenceDates };
    return this.withIdempotency(`schedule-block.update:${blockId}`, payload, (idempotencyKey) =>
      invoke(
        'updateScheduleBlock',
        { ...payload, idempotencyKey },
        z.object({ blockIds: z.array(idSchema) }),
      ),
    );
  }

  closeScheduleBlock(blockId: string, action: 'cancelled' | 'retired') {
    const callableName = action === 'cancelled' ? 'cancelScheduleBlock' : 'retireScheduleBlock';
    const payload = { blockId };
    return this.withIdempotency(`schedule-block.${action}:${blockId}`, payload, (idempotencyKey) =>
      invoke(
        callableName,
        { ...payload, idempotencyKey },
        z.object({ blockIds: z.array(idSchema) }),
      ),
    );
  }

  rescheduleAppointment(appointmentId: string, newStartAt: string) {
    const payload = { appointmentId, newStartAt };
    return this.withIdempotency(
      `appointment.reschedule:${appointmentId}`,
      payload,
      (idempotencyKey) =>
        invoke(
          'rescheduleAppointment',
          { ...payload, idempotencyKey },
          z.object({ sourceAppointmentId: idSchema, appointmentId: idSchema }).passthrough(),
        ),
    );
  }

  cancelAppointment(appointmentId: string, refundAmountCents?: number) {
    const payload =
      refundAmountCents == null ? { appointmentId } : { appointmentId, refundAmountCents };
    return this.withIdempotency(`appointment.cancel:${appointmentId}`, payload, (idempotencyKey) =>
      invoke('cancelAppointment', { ...payload, idempotencyKey }, cancellationMutationResultSchema),
    );
  }

  setExpectedPaymentDate(appointmentId: string, expectedPaymentAt: string) {
    const payload = { appointmentId, expectedPaymentAt };
    return this.withIdempotency(
      `appointment.expected-payment:${appointmentId}`,
      payload,
      (idempotencyKey) =>
        invoke(
          'setExpectedPaymentDate',
          { ...payload, idempotencyKey },
          z
            .object({ appointmentId: idSchema, expectedPaymentAt: isoSchema.nullable() })
            .passthrough(),
        ),
    );
  }

  sendConfirmationEmail(appointmentId: string, resend: boolean) {
    const payload = { appointmentId, resend };
    return this.withIdempotency(`appointment.email:${appointmentId}`, payload, (idempotencyKey) =>
      invoke(
        'sendConfirmationEmail',
        { ...payload, idempotencyKey },
        z
          .object({
            deliveryEventId: idSchema,
            status: z.string(),
            sentAt: isoSchema.nullable().optional(),
          })
          .passthrough(),
      ),
    );
  }

  async loadEmailDeliveryHistory(appointmentId: string): Promise<EmailDeliveryEvent[]> {
    const root = `clinics/${this.session.clinicId}`;
    const events = await listDocuments(
      `${root}/emailDeliveryEvents`,
      [
        where('clinicId', '==', this.session.clinicId),
        where('appointmentId', '==', appointmentId),
        orderBy('requestedAt', 'desc'),
        limit(20),
      ],
      emailDeliveryEventDocumentSchema,
    );
    return events.map(({ id, data: event }) => ({
      id,
      appointmentId: event.appointmentId,
      recipientEmail: event.recipientEmail,
      status: event.status,
      requestedAt: event.requestedAt,
      sentAt: event.sentAt ?? '',
      templateVersion: event.templateVersion,
      attemptNumber: event.attemptNumber,
      resend: id.startsWith('resend_'),
    }));
  }

  recordPayment(appointmentId: string, amountCents: number, method = 'pix') {
    const semanticPayload = { appointmentId, amountCents, method };
    return this.withIdempotency(
      `payment.create:${appointmentId}`,
      semanticPayload,
      (idempotencyKey) =>
        invoke(
          'recordPayment',
          { ...semanticPayload, occurredAt: new Date().toISOString(), idempotencyKey },
          ledgerMutationResultSchema,
        ),
    );
  }

  recordRefund(appointmentId: string, amountCents: number) {
    const semanticPayload = { appointmentId, amountCents };
    return this.withIdempotency(
      `refund.create:${appointmentId}`,
      semanticPayload,
      (idempotencyKey) =>
        invoke(
          'recordRefund',
          { ...semanticPayload, occurredAt: new Date().toISOString(), idempotencyKey },
          ledgerMutationResultSchema,
        ),
    );
  }

  createTask(draft: TaskDraft) {
    const payload = {
      title: draft.title,
      description: draft.description,
      assigneeId: draft.assigneeId,
      priority: draft.priority,
      dueAt: draft.dueAt || null,
      patientId: draft.patientId || null,
      appointmentId: draft.appointmentId || null,
    };
    return this.withIdempotency('task.create', payload, (idempotencyKey) =>
      invoke('createTask', { ...payload, idempotencyKey }, taskMutationResultSchema),
    );
  }

  updateTask(taskId: string, patch: TaskUpdateDraft) {
    const payload = { taskId, ...patch };
    return this.withIdempotency(`task.update:${taskId}`, payload, (idempotencyKey) =>
      invoke('updateTask', { ...payload, idempotencyKey }, taskMutationResultSchema),
    );
  }

  async loadTaskHistory(taskId: string): Promise<TaskActivity[]> {
    const root = `clinics/${this.session.clinicId}`;
    const events = await listDocuments(
      `${root}/tasks/${taskId}/events`,
      [
        where('clinicId', '==', this.session.clinicId),
        where('taskId', '==', taskId),
        orderBy('occurredAt', 'desc'),
        limit(50),
      ],
      taskEventDocumentSchema,
    );
    return events.reverse().map(({ id, data: event }) => {
      return {
        id,
        occurredAt: event.occurredAt,
        actorName:
          this.users.find((user) => user.id === event.actorId)?.displayName ?? 'Profissional',
        text: describeTaskEvent(
          event,
          (userId) => this.users.find((user) => user.id === userId)?.displayName,
        ),
      };
    });
  }

  async updateProfilePhoto(file: File): Promise<{ photoPath: string }> {
    if (!(profilePhotoMimeTypes as readonly string[]).includes(file.type)) {
      throw new Error('Escolha uma imagem JPG, PNG ou WebP.');
    }
    if (file.size <= 0 || file.size > MAX_PROFILE_PHOTO_BYTES) {
      throw new Error('A foto deve ter no máximo 2 MB.');
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        typeof reader.result === 'string'
          ? resolve(reader.result)
          : reject(new Error('Não foi possível ler a foto.'));
      reader.onerror = () => reject(new Error('Não foi possível ler a foto.'));
      reader.readAsDataURL(file);
    });
    const dataBase64 = dataUrl.split(',')[1];
    if (!dataBase64) throw new Error('Não foi possível ler a foto.');
    return invoke(
      'updateProfilePhoto',
      { contentType: file.type, dataBase64 },
      z.object({ photoPath: z.string().min(1).max(512) }),
    );
  }

  async loadProfilePhoto(photoPath: string): Promise<string> {
    const cached = this.attachmentObjectUrls.get(photoPath);
    if (cached) return cached;
    try {
      const blob = await getBlob(ref(getFirebaseServices().storage, photoPath));
      const objectUrl = URL.createObjectURL(blob);
      this.attachmentObjectUrls.set(photoPath, objectUrl);
      return objectUrl;
    } catch (error) {
      throw toPortugueseError(error, 'Não foi possível carregar esta foto de perfil.');
    }
  }

  async sendMessage(conversationId: string, text: string, file?: File, reference?: ChatReference) {
    if (file) {
      if (!(chatAttachmentMimeTypes as readonly string[]).includes(file.type)) {
        throw new Error('Envie uma imagem JPG, PNG ou WebP, ou um arquivo PDF.');
      }
      if (file.size <= 0 || file.size > MAX_CHAT_ATTACHMENT_BYTES) {
        throw new Error('O arquivo deve ter no máximo 10 MB.');
      }
    }
    const semanticPayload = {
      conversationId,
      text,
      file: file
        ? { name: file.name, type: file.type, size: file.size, lastModified: file.lastModified }
        : null,
      reference: reference ?? null,
    };
    return this.withIdempotency(
      `chat.message:${conversationId}`,
      semanticPayload,
      async (clientMessageId) => {
        let attachmentId: string | undefined;
        if (file) {
          const reservation = await invoke(
            'reserveChatAttachment',
            {
              conversationId,
              fileName: file.name,
              contentType: file.type,
              sizeBytes: file.size,
              idempotencyKey: crypto.randomUUID(),
            },
            attachmentReservationSchema,
          );
          const { storage } = getFirebaseServices();
          await uploadBytes(ref(storage, reservation.storagePath), file, {
            contentType: file.type,
            customMetadata: reservation.requiredMetadata,
          });
          await invoke(
            'finalizeChatAttachment',
            { reservationId: reservation.reservationId },
            z.object({ reservationId: idSchema, status: z.literal('ready') }),
          );
          attachmentId = reservation.reservationId;
        }
        return invoke(
          'sendChatMessage',
          {
            conversationId,
            clientMessageId,
            text,
            ...(attachmentId ? { attachmentId } : {}),
            ...(reference ? { reference } : {}),
          },
          z.object({ messageId: idSchema }).passthrough(),
        );
      },
    );
  }

  subscribeToConversationSummaries(
    onSummaries: (summaries: ConversationSummary[]) => void,
    onUnreadCounts: (unreadCounts: Readonly<Record<string, number>>) => void,
    onError: (message: string) => void,
  ): Unsubscribe {
    const { db } = getFirebaseServices();
    const root = `clinics/${this.session.clinicId}`;
    const clinicFilter = where('clinicId', '==', this.session.clinicId);
    let directDocuments: QueryDocumentSnapshot<DocumentData>[] | null = null;
    let clinicDocuments: QueryDocumentSnapshot<DocumentData>[] | null = null;
    const readStateUnsubscribers = new Map<string, Unsubscribe>();
    const unreadCounts = new Map<string, number>();
    let unreadNotificationQueued = false;
    let disposed = false;

    const scheduleUnreadNotification = () => {
      if (unreadNotificationQueued) return;
      unreadNotificationQueued = true;
      queueMicrotask(() => {
        unreadNotificationQueued = false;
        if (!disposed) onUnreadCounts(Object.fromEntries(unreadCounts));
      });
    };

    const syncSummaries = () => {
      if (!directDocuments || !clinicDocuments) return;
      try {
        const summaryById = new Map(
          [...directDocuments, ...clinicDocuments].map((conversationDocument) => {
            const item = parseRemote(
              conversationDocumentSchema,
              normalizeFirestoreValue(conversationDocument.data()),
            );
            const summary = conversationSummaryFromWire(conversationDocument.id, item);
            return [summary.id, summary] as const;
          }),
        );

        for (const [conversationId, unsubscribe] of readStateUnsubscribers) {
          if (summaryById.has(conversationId)) continue;
          unsubscribe();
          readStateUnsubscribers.delete(conversationId);
          unreadCounts.delete(conversationId);
        }

        for (const conversationId of summaryById.keys()) {
          if (readStateUnsubscribers.has(conversationId)) continue;
          const unsubscribe = onSnapshot(
            doc(db, `${root}/conversations/${conversationId}/readStates/${this.session.id}`),
            (snapshot) => {
              if (!snapshot.exists()) {
                unreadCounts.set(conversationId, 0);
                scheduleUnreadNotification();
                return;
              }
              try {
                const readState = parseRemote(
                  conversationReadStateDocumentSchema,
                  normalizeFirestoreValue(snapshot.data()),
                );
                unreadCounts.set(conversationId, readState.unreadCount);
                scheduleUnreadNotification();
              } catch {
                onError('Não foi possível atualizar a contagem de mensagens não lidas.');
              }
            },
            () => onError('Não foi possível atualizar a contagem de mensagens não lidas.'),
          );
          readStateUnsubscribers.set(conversationId, unsubscribe);
        }

        onSummaries([...summaryById.values()]);
      } catch {
        onError('Não foi possível atualizar a lista de conversas em tempo real.');
      }
    };

    const directUnsubscribe = onSnapshot(
      query(
        collection(db, `${root}/conversations`),
        clinicFilter,
        where('type', '==', 'direct'),
        where('active', '==', true),
        where('participantIds', 'array-contains', this.session.id),
        limit(50),
      ),
      (snapshot) => {
        directDocuments = snapshot.docs;
        syncSummaries();
      },
      () => onError('A atualização das conversas diretas foi interrompida. Tente novamente.'),
    );
    const clinicUnsubscribe = onSnapshot(
      query(
        collection(db, `${root}/conversations`),
        clinicFilter,
        where('type', '==', 'clinic'),
        where('active', '==', true),
        limit(20),
      ),
      (snapshot) => {
        clinicDocuments = snapshot.docs;
        syncSummaries();
      },
      () => onError('A atualização das conversas da clínica foi interrompida. Tente novamente.'),
    );

    return () => {
      disposed = true;
      directUnsubscribe();
      clinicUnsubscribe();
      for (const unsubscribe of readStateUnsubscribers.values()) unsubscribe();
      readStateUnsubscribers.clear();
    };
  }

  subscribeToMessages(
    conversationId: string,
    onMessages: (messages: Conversation['messages']) => void,
    onError: (message: string) => void,
  ): Unsubscribe {
    const root = `clinics/${this.session.clinicId}`;
    const messagesQuery = query(
      collection(getFirebaseServices().db, `${root}/conversations/${conversationId}/messages`),
      where('clinicId', '==', this.session.clinicId),
      where('conversationId', '==', conversationId),
      orderBy('sentAt', 'desc'),
      orderBy(documentId(), 'desc'),
      limit(30),
    );
    let firstPage = true;
    return onSnapshot(
      messagesQuery,
      (snapshot) => {
        if (firstPage) {
          const cursor = snapshot.docs.at(-1);
          if (cursor) this.messageCursors.set(conversationId, cursor);
          firstPage = false;
        }
        void Promise.all(
          [...snapshot.docs]
            .reverse()
            .map((messageDocument) =>
              this.toChatMessage(
                messageDocument.id,
                parseRemote(messageDocumentSchema, normalizeFirestoreValue(messageDocument.data())),
              ),
            ),
        )
          .then(onMessages)
          .catch(() => onError('Não foi possível atualizar as mensagens em tempo real.'));
      },
      () => onError('A conexão em tempo real foi interrompida. Tente novamente.'),
    );
  }

  async loadOlderMessages(
    conversationId: string,
  ): Promise<{ messages: Conversation['messages']; hasMore: boolean }> {
    const cursor = this.messageCursors.get(conversationId);
    if (!cursor) return { messages: [], hasMore: false };
    const root = `clinics/${this.session.clinicId}`;
    const snapshot = await getDocs(
      query(
        collection(getFirebaseServices().db, `${root}/conversations/${conversationId}/messages`),
        where('clinicId', '==', this.session.clinicId),
        where('conversationId', '==', conversationId),
        orderBy('sentAt', 'desc'),
        orderBy(documentId(), 'desc'),
        startAfter(cursor),
        limit(30),
      ),
    );
    const nextCursor = snapshot.docs.at(-1);
    if (nextCursor) this.messageCursors.set(conversationId, nextCursor);
    const messages = await Promise.all(
      [...snapshot.docs]
        .reverse()
        .map((messageDocument) =>
          this.toChatMessage(
            messageDocument.id,
            parseRemote(messageDocumentSchema, normalizeFirestoreValue(messageDocument.data())),
          ),
        ),
    );
    return { messages, hasMore: snapshot.docs.length === 30 };
  }

  markConversationRead(conversationId: string, messageId: string) {
    return invoke(
      'markConversationRead',
      { conversationId, messageId },
      z.object({ conversationId: idSchema, unreadCount: z.literal(0) }),
    );
  }

  async markNotificationsRead(notificationIds: string[]): Promise<void> {
    const root = `clinics/${this.session.clinicId}`;
    await Promise.all(
      notificationIds.map((notificationId) =>
        updateDoc(doc(getFirebaseServices().db, `${root}/notifications/${notificationId}`), {
          readAt: serverTimestamp(),
        }),
      ),
    );
  }
}
